import { driver, Driver, DriveStep } from "driver.js";
import "driver.js/dist/driver.css";
import { useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { UserContext } from "../routes/routes";
import { RoleTypeCode } from "../utils/Constant";
import { tourStepsConfigAdmin } from "./tourStepsConfigAdmin";
import { tourStepsConfigSuperAdmin } from "./tourStepsConfigSuperAdmin";
import { tourStepsConfigUser } from "./tourStepsConfigUser";

interface NavTourProps {
  isActive: boolean;
  onClose?: () => void;
  data: any;
  isAddIconVisible: boolean;
}

const Tour: React.FC<NavTourProps> = ({
  isActive,
  onClose,
  data,
  isAddIconVisible,
}) => {
  const driverObj = useRef<Driver | null>(null);
  const { appContextdata } = useContext<any>(UserContext);
  const [role, setRole] = useState<any>("");

  const fetchRole = () => {
    if (appContextdata?.Role === RoleTypeCode?.SuperAdmin) {
      setRole("SuperAdmin");
    } else if (appContextdata?.Role === RoleTypeCode?.Administrator) {
      setRole("Admin");
    } else if (appContextdata?.Role === RoleTypeCode?.User) {
      setRole("User");
    } else if (appContextdata?.Role === RoleTypeCode?.Coordinator) {
      setRole("Coordinator");
    } else if (appContextdata?.Role === RoleTypeCode?.Responder) {
      setRole("Responder");
    }
  };

  useEffect(() => {
    if (appContextdata?.Role) {
      fetchRole();
    }
  }, [appContextdata?.Role]);

  const location = useLocation();
  const currentPath = location.pathname;

  const pathSegments = currentPath.split("/").filter(Boolean);

  const basePath = "/" + pathSegments.slice(0, 2).join("/");

  let steps: DriveStep[] = [];

  const restrictedPaths = [
    "/user-department",
    "/user-designation",
    "/location",
    "/user-classification",
    "/question-types",
    "/assessment-types",
  ];

  if (role === "Admin") {
    const pathWithAddFallbacks: Record<string, string> = {
      "/users": "/users-add",
      "/user-department": "/user-department-add",
      "/user-classification": "/user-classification-add",
      "/user-designation": "/user-designation-add",
      "/location": "/location-add",
      "/assessment-types": "/assessment-types-add",
      "/question-types": "/question-types-add",
      "/questions": "/questions-add",
      "/forms": "/forms-add",
      "/quiz": "/quiz-add",
      "/poll": "/poll-add",
      "/form-assignment": "/form-assignment-add",
      "/form-assignment/poll": "/form-assignment/poll-add",
    };

    const fallbackPath = pathWithAddFallbacks[basePath];

    if (restrictedPaths.includes(basePath) && !isAddIconVisible) {
      steps = [];
    } else if (fallbackPath) {
      steps =
        data?.length > 0
          ? tourStepsConfigAdmin[basePath] || []
          : tourStepsConfigAdmin[fallbackPath] || [];
    } else {
      steps = tourStepsConfigAdmin[basePath] || [];
    }
  } else if (role == "User") {
    steps = tourStepsConfigUser[basePath] || [];
  } else if (role === "SuperAdmin") {
    const pathWithAddFallbacks: { [key: string]: string } = {
      "/users": "/users-add",
      "/user-department": "/user-department-add",
      "/user-classification": "/user-classification-add",
      "/user-designation": "/user-designation-add",
      "/location": "/location-add",
      "assessment-types": "/assessment-types-add",
      "/question-types": "/question-types-add",
      "/questions": "/questions-add",
      "/forms": "/forms-add",
      "/quiz": "/quiz-add",
      "/poll": "/poll-add",
      "/form-assignment": "/form-assignment-add",
      "/form-assignment/poll": "/form-assignment/poll-add",
    };

    const fallbackPath = pathWithAddFallbacks[basePath];

    if (restrictedPaths.includes(basePath) && !isAddIconVisible) {
      steps = [];
    } else if (fallbackPath) {
      steps =
        data?.length > 0
          ? tourStepsConfigSuperAdmin[basePath] || []
          : tourStepsConfigSuperAdmin[fallbackPath] || [];
    } else {
      steps = tourStepsConfigSuperAdmin[basePath] || [];
    }
  }

  useEffect(() => {
    driverObj.current = driver({
      showProgress: true,
      animate: true,
      showButtons: ["next", "previous", "close"],
      steps: steps,
      popoverClass: "custom-driver-theme",
      overlayColor: "rgba(0, 0, 0, 0.7)",
      nextBtnText: "Next",
      prevBtnText: "Back",

      onDestroyStarted: () => {
        if (onClose) onClose();
      },
      onDestroyed: () => {
        if (onClose) onClose();
      },
    });

    if (isActive && driverObj.current) {
      setTimeout(() => {
        driverObj.current?.drive();
      }, 300);
    }

    return () => {
      if (driverObj.current) {
        driverObj.current.destroy();
      }
    };
  }, [isActive, onClose]);

  return null;
};

export default Tour;
